import type {
  CollectionEntityDefinition,
  ActionDefinition,
  ModuleContext
} from '@gzl10/nexus-sdk'
import type { Context, MiddlewareHandler } from 'hono'
import { useIdField, useTextField, useTextareaField, useSelectField, useNumberField, useJsonField, useDatetimeField, useDescriptionField, useEnabledField, useNameField, useCronField } from '@gzl10/nexus-sdk/fields'
import cron from 'node-cron'
import type { Schedule } from './schedules.types.js'
import type { SchedulesService } from './schedules.service.js'

/** Typed accessor for SchedulesService from context */
const getService = (ctx: ModuleContext) => ctx.services.get<SchedulesService>('scheduler')

function getReqInfo(c?: Context<any>) {
  const fwd = c?.req.header('x-forwarded-for')
  const ip = fwd?.split(',')[0]?.trim() || c?.req.header('x-real-ip')
  const userAgent = c?.req.header('user-agent')
  return { ip, userAgent }
}

/**
 * Schedule Entity (collection)
 */
export const scheduleEntity: CollectionEntityDefinition = {
  type: 'collection',
  realtime: 'sync',
  taggeable: true,
  table: 'schedules',
  routePrefix: '/',
  label: { en: 'Schedules', es: 'Programaciones' },
  labelPlural: { en: 'Schedules', es: 'Programaciones' },
  labelField: 'name',
  timestamps: true,
  audit: true,
  defaultSort: { field: 'last_run_at', order: 'desc' },
  availableDisplayModes: ['table', 'calendar'],
  calendarFrom: 'last_run_at',

  fields: {
    id: useIdField(),
    name: useNameField({ meta: { sortable: true, searchable: true } }),
    description: useDescriptionField({ meta: { searchable: true, showInDisplay: false } }),
    cron_expression: useCronField({ meta: { searchable: true } }),
    timezone: useSelectField({
      label: { en: 'Timezone', es: 'Zona horaria' },
      master: 'timezones',
      required: true,
      defaultValue: 'UTC',
      meta: { showInDisplay: false }
    }),
    function_key: {
      label: { en: 'Function', es: 'Función' },
      input: 'select',
      required: true,
      db: { type: 'string', size: 100, nullable: false },
      options: { endpoint: '/schedules/functions', valueField: 'id', labelField: 'name' },
      meta: { searchable: true, sortable: true }
    },
    function_args: useJsonField({
      label: { en: 'Function Arguments', es: 'Argumentos de función' },
      meta: { showInDisplay: false }
    }),
    enabled: useEnabledField({ meta: { sortable: true } }),
    status: useSelectField({
      label: { en: 'Status', es: 'Estado' },
      options: [
        { value: 'idle', label: { en: 'Idle', es: 'Inactivo' }, icon: 'mdi:clock-outline' },
        { value: 'running', label: { en: 'Running', es: 'Ejecutándose' }, icon: 'mdi:play-circle' },
        { value: 'paused', label: { en: 'Paused', es: 'Pausado' }, icon: 'mdi:pause-circle' },
        { value: 'error', label: { en: 'Error', es: 'Error' }, icon: 'mdi:alert-circle' },
        { value: 'retrying', label: { en: 'Retrying', es: 'Reintentando' }, icon: 'mdi:refresh-circle' }
      ],
      defaultValue: 'idle',
      size: 20,
      displayProps: { align: 'center' },
      meta: { sortable: true, showInForm: false }
    }),
    last_run_at: useDatetimeField({ label: { en: 'Last Run', es: 'Última ejecución' }, meta: { sortable: true, showInForm: false } }),
    last_error: useTextareaField({ label: { en: 'Last Error', es: 'Último error' }, meta: { showInForm: false, showInDisplay: false } }),
    run_count: useNumberField({ label: { en: 'Run Count', es: 'Número de ejecuciones' }, defaultValue: 0, meta: { sortable: true, showInForm: false, showInDisplay: false } }),
    max_retries: useNumberField({ label: { en: 'Max Retries', es: 'Reintentos máximos' }, defaultValue: 0, validation: { min: 0, max: 10 }, meta: { showInDisplay: false } }),
    retry_count: useNumberField({ label: { en: 'Retry Count', es: 'Reintentos actuales' }, defaultValue: 0, meta: { showInForm: false, showInDisplay: false } }),
    retry_delay_ms: useNumberField({ label: { en: 'Retry Delay (ms)', es: 'Delay reintento (ms)' }, defaultValue: 5000, validation: { min: 1000, max: 300000 }, meta: { showInDisplay: false } }),
    retry_backoff: useSelectField({
      label: { en: 'Retry Backoff', es: 'Estrategia reintento' },
      options: [
        { value: 'exponential', label: { en: 'Exponential', es: 'Exponencial' } },
        { value: 'fixed', label: { en: 'Fixed', es: 'Fijo' } },
        { value: 'linear', label: { en: 'Linear', es: 'Lineal' } }
      ],
      defaultValue: 'exponential',
      size: 20,
      meta: { showInDisplay: false }
    })
  },

  actions: [
    {
      key: 'pause',
      label: { en: 'Pause', es: 'Pausar' },
      icon: 'mdi:pause',
      scope: 'row',
      method: 'POST',
      select: ['id', 'name', 'status', 'enabled'],
      disabled: { field: 'status', $in: ['paused', 'running'] },
      disabledReason: { en: 'Schedule is already paused or running', es: 'La programación ya está pausada o ejecutándose' },
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        const { _record } = input as { _record: Schedule }
        const service = getService(ctx)
        await service.pause(_record.id)
        const { ip, userAgent } = getReqInfo(c)
        ctx.events.notify('audit.log', {
          source: 'module:schedules',
          action: 'schedule.paused',
          actorId: (input as Record<string, unknown>)['_authUserId'] as string | undefined,
          resourceType: 'schedule',
          resourceId: _record.id,
          ip,
          userAgent,
          metadata: { name: _record.name }
        })
        return { success: true, status: 'paused' }
      },
      casl: { action: 'update' }
    },
    {
      key: 'resume',
      label: { en: 'Resume', es: 'Reanudar' },
      icon: 'mdi:play',
      scope: 'row',
      method: 'POST',
      select: ['id', 'name', 'status', 'enabled', 'cron_expression', 'timezone'],
      disabled: { field: 'status', $ne: 'paused' },
      disabledReason: { en: 'Schedule is not paused', es: 'La programación no está pausada' },
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        const { _record } = input as { _record: Schedule }
        const service = getService(ctx)
        await service.resume(_record.id)
        const { ip, userAgent } = getReqInfo(c)
        ctx.events.notify('audit.log', {
          source: 'module:schedules',
          action: 'schedule.resumed',
          actorId: (input as Record<string, unknown>)['_authUserId'] as string | undefined,
          resourceType: 'schedule',
          resourceId: _record.id,
          ip,
          userAgent,
          metadata: { name: _record.name }
        })
        return { success: true, status: 'idle' }
      },
      casl: { action: 'update' }
    },
    {
      key: 'trigger',
      label: { en: 'Run Now', es: 'Ejecutar ahora' },
      icon: 'mdi:play-circle',
      scope: 'row',
      variant: 'primary',
      method: 'POST',
      select: ['id', 'name', 'status', 'function_key', 'function_args'],
      disabled: { field: 'status', $eq: 'running' },
      disabledReason: { en: 'Schedule is already running', es: 'La programación ya está ejecutándose' },
      output: {
        success: useEnabledField(),
        duration_ms: useNumberField({ label: { en: 'Duration (ms)', es: 'Duración (ms)' } }),
        error: useTextField({ label: { en: 'Error', es: 'Error' } })
      },
      confirm: {
        type: 'simple',
        message: { en: 'This will execute the schedule immediately.', es: 'Esto ejecutará la programación inmediatamente.' },
        severity: 'info'
      },
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        const { _record } = input as { _record: Schedule }
        const service = getService(ctx)
        const result = await service.trigger(_record.id)
        const { ip, userAgent } = getReqInfo(c)
        ctx.events.notify('audit.log', {
          source: 'module:schedules',
          action: 'schedule.triggered',
          actorId: (input as Record<string, unknown>)['_authUserId'] as string | undefined,
          resourceType: 'schedule',
          resourceId: _record.id,
          ip,
          userAgent,
          metadata: { name: _record.name }
        })
        return { success: result.success, duration_ms: result.duration_ms, error: result.error ?? null }
      },
      casl: { action: 'execute' }
    }
  ],

  casl: {
    subject: 'Schedule',
    permissions: {
      SUPPORT: { actions: ['read', 'execute'] }
    }
  },

  hooks: (ctx) => {
    const getService = () => ctx.services.get('scheduler') as import('./schedules.service.js').SchedulesService

    function validateScheduleFields(data: Record<string, unknown>) {
      const { ValidationError } = ctx.core.errors

      if (data['cron_expression'] !== undefined) {
        if (!cron.validate(data['cron_expression'] as string)) {
          throw new ValidationError(`Invalid cron expression: ${data['cron_expression']}`)
        }
      }

      if (data['timezone'] !== undefined && data['timezone'] !== 'UTC') {
        try {
          Intl.DateTimeFormat(undefined, { timeZone: data['timezone'] as string })
        } catch {
          throw new ValidationError(`Invalid timezone: ${data['timezone']}`)
        }
      }

      if (data['function_key'] !== undefined) {
        if (typeof data['function_key'] !== 'string' || !data['function_key']) {
          throw new ValidationError('function_key is required')
        }
      }
    }

    return {
      beforeCreate: async (data) => {
        const d = data as Record<string, unknown>
        // Apply server-side defaults for internal fields
        if (d['status'] === undefined) d['status'] = 'idle'
        if (d['run_count'] === undefined) d['run_count'] = 0
        if (d['retry_count'] === undefined) d['retry_count'] = 0
        if (d['retry_delay_ms'] === undefined) d['retry_delay_ms'] = 5000
        if (d['retry_backoff'] === undefined) d['retry_backoff'] = 'exponential'
        if (d['max_retries'] === undefined) d['max_retries'] = 0
        validateScheduleFields(d)
        return d
      },
      beforeUpdate: async (_id, data) => {
        const d = data as Record<string, unknown>
        validateScheduleFields(d)
        return d
      },
      afterCreate: async (record) => {
        try {
          const r = record as Record<string, unknown>
          if (r['id']) await getService().syncJob(r['id'] as string)
        } catch (error) {
          ctx.core.logger.error(`Failed to sync schedule after create: ${error instanceof Error ? error.message : error}`)
        }
      },
      afterUpdate: async (record) => {
        try {
          const r = record as Record<string, unknown>
          if (r['id']) await getService().syncJob(r['id'] as string)
        } catch (error) {
          ctx.core.logger.error(`Failed to sync schedule after update: ${error instanceof Error ? error.message : error}`)
        }
      },
      afterDelete: async (id) => {
        getService().cancelJobById(id)
      }
    }
  }
}

/**
 * Schedule Log Entity (event - append-only)
 */
export const scheduleLogEntity: CollectionEntityDefinition = {
  type: 'collection',
  realtime: 'sync',
  immutable: true,
  allowCreate: false,
  allowEdit: false,
  allowDelete: false,
  table: 'schedule_logs',
  label: { en: 'Logs', es: 'Logs' },
  labelField: 'schedule_id',
  timestamps: true,
  retention: { days: 30 },
  defaultSort: { field: 'started_at', order: 'desc' },
  availableDisplayModes: ['table'],

  fields: {
    id: useIdField(),
    schedule_id: useSelectField({
      label: { en: 'Schedule', es: 'Programación' },
      table: 'schedules',
      endpoint: '/schedules',
      labelField: 'name',
      onDelete: 'CASCADE',
      required: true,
      size: 26,
      meta: { sortable: true }
    }),
    started_at: useDatetimeField({ label: { en: 'Started At', es: 'Iniciado en' }, required: true, meta: { sortable: true } }),
    finished_at: useDatetimeField({ label: { en: 'Finished At', es: 'Finalizado en' } }),
    duration_ms: useNumberField({ label: { en: 'Duration (ms)', es: 'Duración (ms)' }, meta: { sortable: true } }),
    status: useSelectField({
      label: { en: 'Status', es: 'Estado' },
      options: [
        { value: 'success', label: { en: 'Success', es: 'Éxito' } },
        { value: 'error', label: { en: 'Error', es: 'Error' } },
        { value: 'skipped', label: { en: 'Skipped', es: 'Omitido' } },
        { value: 'retrying', label: { en: 'Retrying', es: 'Reintentando' } },
        { value: 'exhausted', label: { en: 'Exhausted', es: 'Agotado' } }
      ],
      required: true,
      size: 20,
      meta: { sortable: true }
    }),
    result: useJsonField({ label: { en: 'Result', es: 'Resultado' }, meta: { showInDisplay: false } }),
    error: useTextareaField({ label: { en: 'Error', es: 'Error' }, meta: { showInDisplay: false } })
  },

  actions: [
    {
      key: 'clear-logs',
      label: { en: 'Clear All Logs', es: 'Limpiar todos los registros' },
      icon: 'mdi:delete-sweep',
      scope: 'entity',
      method: 'DELETE',
      variant: 'danger',
      confirm: {
        type: 'simple',
        title: { en: 'Clear all logs?', es: '¿Limpiar todos los registros?' },
        message: { en: 'This will permanently delete all schedule execution logs. This action cannot be undone.', es: 'Esto eliminará permanentemente todos los registros de ejecución. Esta acción no se puede deshacer.' },
        confirmText: { en: 'Clear All', es: 'Limpiar todo' }
      },
      middleware: (ctx) => [ctx.core.middleware['auth'] as MiddlewareHandler, requireAdmin(ctx)],
      handler: async (ctx) => {
        const { knex } = ctx.db
        const deleted = await knex('schedule_logs').delete()
        return { deleted }
      }
    }
  ],

  casl: {
    subject: 'ScheduleLog',
    permissions: {
      SUPPORT: { actions: ['read'] }
    }
  }
}

// ============================================================================
// MODULE ACTIONS
// ============================================================================

/**
 * Middleware para verificar ADMIN
 */
function requireAdmin(ctx: ModuleContext): MiddlewareHandler {
  const { UnauthorizedError, ForbiddenError } = ctx.core.errors
  return async (c, next) => {
    const user = c.get('user') as { id?: string } | undefined
    const ability = c.get('ability') as { can?: (a: string, s: string) => boolean } | undefined
    if (!user) throw new UnauthorizedError('AUTH_REQUIRED')
    if (!ability?.can?.('manage', 'all')) throw new ForbiddenError('FORBIDDEN')
    await next()
  }
}

/**
 * Action: next-runs
 * GET /schedules/next-runs - Gets upcoming scheduled runs
 */
export const nextRunsAction: ActionDefinition = {
  key: 'next-runs',
  label: { en: 'Get Next Runs', es: 'Obtener próximas ejecuciones' },
  icon: 'mdi:calendar-clock',
  scope: 'module',
  method: 'GET',
  hidden: true,
  output: {},
  middleware: (ctx) => [ctx.core.middleware['auth'] as MiddlewareHandler, requireAdmin(ctx)],
  input: {
    limit: useNumberField({ label: { en: 'Limit', es: 'Límite' }, validation: { min: 1, max: 100 } })
  },
  handler: async (ctx, input, c?: Context<any>) => {
    const { limit: inputLimit } = input as { limit?: number }
    const queryLimit = parseInt(c?.req.query('limit') ?? '') || 10
    const limit = Math.min(inputLimit ?? queryLimit, 100)

    const service = getService(ctx)
    const nextRuns = await service.getNextRuns(limit)

    return {
      items: nextRuns.map(r => ({
        id: r.id,
        name: r.name,
        cron_expression: r.cron_expression,
        next_run: new Date(r.next_run).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
      })),
      total: nextRuns.length
    }
  }
}

/**
 * Action: functions
 * GET /schedules/functions - Lists registered functions for function_registry
 */
export const functionsAction: ActionDefinition = {
  key: 'functions',
  label: { en: 'Get Registered Functions', es: 'Obtener funciones registradas' },
  icon: 'mdi:function',
  scope: 'module',
  method: 'GET',
  hidden: true,
  output: {},
  middleware: (ctx) => [ctx.core.middleware['auth'] as MiddlewareHandler, requireAdmin(ctx)],
  handler: async (ctx) => {
    const service = getService(ctx)
    const functions = service.getRegisteredFunctions()

    return {
      items: functions,
      total: functions.length
    }
  }
}
